import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  PieChart, Pie, Cell, RadialBarChart, RadialBar, Legend
} from 'recharts';
import { format, parseISO } from 'date-fns';
import {
  Shield, CheckCircle, AlertTriangle, XCircle, Clock, FileText,
  ArrowLeft, RefreshCw, Bell, AlertCircle, Calendar, Activity,
  FolderOpen, Target, TrendingUp, ChevronRight, Loader2, Info
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ZONE_COLORS = {
  zone_01: '#3B82F6',
  zone_02: '#8B5CF6',
  zone_03: '#EF4444',
  zone_04: '#10B981',
  zone_05: '#F59E0B',
  zone_06: '#06B6D4',
  zone_10: '#EC4899',
  zone_11: '#6366F1',
  zone_12: '#14B8A6'
};

const SEVERITY_COLORS = {
  critical: 'bg-red-500/20 text-red-600 border-red-500/30',
  high: 'bg-orange-500/20 text-orange-600 border-orange-500/30',
  medium: 'bg-amber-500/20 text-amber-600 border-amber-500/30',
  low: 'bg-blue-500/20 text-blue-600 border-blue-500/30',
  info: 'bg-slate-500/20 text-slate-600 border-slate-500/30'
};

const STATUS_ICONS = {
  complete: CheckCircle,
  incomplete: AlertTriangle,
  critical: XCircle
};

export default function TMFHealthPage() {
  const { getAuthHeaders, selectedStudy: contextStudy } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [studies, setStudies] = useState([]);
  const [selectedStudy, setSelectedStudy] = useState(contextStudy?.study_id || 'all');
  const [dashboardData, setDashboardData] = useState(null);
  const [studyHealth, setStudyHealth] = useState(null);

  // Set selected study from context when it changes
  useEffect(() => {
    if (contextStudy?.study_id) {
      setSelectedStudy(contextStudy.study_id);
    }
  }, [contextStudy]);
  const [alerts, setAlerts] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { headers } = getAuthHeaders();

      // Fetch studies
      const studiesRes = await axios.get(`${API_URL}/api/studies`, { headers });
      setStudies(studiesRes.data || []);

      // Fetch TMF health dashboard
      const dashboardRes = await axios.get(`${API_URL}/api/tmf-health/dashboard`, { headers });
      setDashboardData(dashboardRes.data);

      // Fetch alerts
      const alertsRes = await axios.get(`${API_URL}/api/tmf-health/alerts?limit=100`, { headers });
      setAlerts(alertsRes.data?.alerts || []);

      // If a specific study is selected, fetch its detailed health
      if (selectedStudy !== 'all') {
        const healthRes = await axios.get(`${API_URL}/api/tmf-health/study/${selectedStudy}`, { headers });
        setStudyHealth(healthRes.data);
      } else {
        setStudyHealth(null);
      }

    } catch (error) {
      console.error('Error fetching TMF health data:', error);
      toast.error('Failed to load TMF health data');
    } finally {
      setLoading(false);
    }
  }, [selectedStudy, getAuthHeaders]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const getReadinessColor = (status) => {
    switch (status) {
      case 'ready': return 'bg-green-500';
      case 'needs_attention': return 'bg-amber-500';
      case 'at_risk': return 'bg-orange-500';
      case 'not_ready': return 'bg-red-500';
      default: return 'bg-slate-500';
    }
  };

  const getReadinessBgColor = (status) => {
    switch (status) {
      case 'ready': return 'bg-green-500/20 text-green-600';
      case 'needs_attention': return 'bg-amber-500/20 text-amber-600';
      case 'at_risk': return 'bg-orange-500/20 text-orange-600';
      case 'not_ready': return 'bg-red-500/20 text-red-600';
      default: return 'bg-slate-500/20 text-slate-600';
    }
  };

  const getZoneBarData = () => {
    if (!studyHealth?.zone_health) return [];
    
    return Object.entries(studyHealth.zone_health).map(([zoneId, data]) => ({
      zone: data.name?.split(' ')[0] || zoneId,
      completeness: data.completeness || 0,
      fill: ZONE_COLORS[zoneId] || '#64748B'
    }));
  };

  const getStatusPieData = () => {
    if (!dashboardData?.overall_stats) return [];
    
    return [
      { name: 'Inspection Ready', value: dashboardData.overall_stats.inspection_ready || 0, color: '#10B981' },
      { name: 'Needs Attention', value: dashboardData.overall_stats.needs_attention || 0, color: '#F59E0B' },
      { name: 'At Risk', value: dashboardData.overall_stats.at_risk || 0, color: '#F97316' },
      { name: 'Not Ready', value: dashboardData.overall_stats.not_ready || 0, color: '#EF4444' }
    ].filter(d => d.value > 0);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-100 via-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-16 h-16 animate-spin text-blue-500 mx-auto mb-4" />
          <p className="text-slate-600">Loading TMF health data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-100 via-gray-50 to-gray-100 relative overflow-hidden">
      {/* Animated background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 right-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-0 left-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
      </div>

      {/* Header */}
      <header className="relative z-10 border-b border-slate-700/50 bg-gradient-to-r from-slate-900 to-slate-800 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/dashboard?view=all')}
                className="text-white/70 hover:text-white hover:bg-slate-50/10"
                data-testid="back-btn"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <div className="h-6 w-px bg-slate-50/20"></div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                  <Activity className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-white">TMF Health Dashboard</h1>
                  <p className="text-xs text-white/60">Document Completeness & Alerts</p>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-3">
              {/* Study Display - Read-only when context study is set */}
              {contextStudy?.study_id ? (
                <div className="flex items-center gap-2 px-3 py-2 rounded-md bg-slate-50/10 border border-white/20" data-testid="study-locked">
                  <div className="flex flex-col">
                    <span className="text-sm font-medium text-white truncate max-w-[180px]">{contextStudy.study_name}</span>
                    <span className="text-[10px] text-white/60">{contextStudy.protocol_number}</span>
                  </div>
                  <Badge variant="outline" className="text-[10px] text-white/70 border-white/30">Locked</Badge>
                </div>
              ) : (
                <Select value={selectedStudy} onValueChange={setSelectedStudy}>
                  <SelectTrigger className="w-48 bg-slate-50/10 border-white/20 text-white" data-testid="study-select">
                    <SelectValue placeholder="Select Study" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    <SelectItem value="all" className="text-white hover:bg-slate-700">All Studies</SelectItem>
                    {studies.map(study => (
                      <SelectItem key={study.id} value={study.id} className="text-white hover:bg-slate-700">
                        {study.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}

              <Button
                variant="outline"
                size="sm"
                onClick={fetchData}
                className="border-white/20 text-white hover:bg-slate-50/10"
                data-testid="refresh-btn"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="relative z-10 max-w-7xl mx-auto px-6 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-slate-100/80 border border-slate-300 mb-6">
            <TabsTrigger value="overview" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white" data-testid="tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="zones" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white" data-testid="tab-zones">Zone Details</TabsTrigger>
            <TabsTrigger value="alerts" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white" data-testid="tab-alerts">
              Alerts <Badge className="ml-2 bg-red-500 text-white">{alerts.filter(a => a.severity === 'critical' || a.severity === 'high').length}</Badge>
            </TabsTrigger>
            <TabsTrigger value="missing" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white" data-testid="tab-missing">Missing Documents</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6" data-testid="overview-content">
            {/* Stats Cards */}
            <div className="grid grid-cols-4 gap-4">
              <Card className="bg-slate-50/90 border-slate-300 backdrop-blur-sm">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-slate-600 text-sm">Total Studies</p>
                      <p className="text-4xl font-bold text-slate-900 mt-1" data-testid="total-studies">
                        {dashboardData?.total_studies || 0}
                      </p>
                    </div>
                    <div className="w-14 h-14 rounded-xl bg-blue-500/20 flex items-center justify-center">
                      <FolderOpen className="w-7 h-7 text-blue-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-slate-50/90 border-slate-300 backdrop-blur-sm">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-slate-600 text-sm">Inspection Ready</p>
                      <p className="text-4xl font-bold text-green-600 mt-1">
                        {dashboardData?.overall_stats?.inspection_ready || 0}
                      </p>
                    </div>
                    <div className="w-14 h-14 rounded-xl bg-green-500/20 flex items-center justify-center">
                      <CheckCircle className="w-7 h-7 text-green-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-slate-50/90 border-slate-300 backdrop-blur-sm">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-slate-600 text-sm">At Risk</p>
                      <p className="text-4xl font-bold text-orange-600 mt-1">
                        {(dashboardData?.overall_stats?.at_risk || 0) + (dashboardData?.overall_stats?.not_ready || 0)}
                      </p>
                    </div>
                    <div className="w-14 h-14 rounded-xl bg-orange-500/20 flex items-center justify-center">
                      <AlertTriangle className="w-7 h-7 text-orange-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-slate-50/90 border-slate-300 backdrop-blur-sm">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-slate-600 text-sm">Critical Alerts</p>
                      <p className="text-4xl font-bold text-red-600 mt-1">
                        {alerts.filter(a => a.severity === 'critical').length}
                      </p>
                    </div>
                    <div className="w-14 h-14 rounded-xl bg-red-500/20 flex items-center justify-center">
                      <Bell className="w-7 h-7 text-red-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Charts Row */}
            <div className="grid grid-cols-2 gap-6">
              {/* Study Health Overview */}
              <Card className="bg-slate-50/90 border-slate-300 backdrop-blur-sm">
                <CardHeader>
                  <CardTitle className="text-slate-900 flex items-center gap-2">
                    <Target className="w-5 h-5 text-blue-600" />
                    Study Health Overview
                  </CardTitle>
                  <CardDescription>TMF completeness by study</CardDescription>
                </CardHeader>
                <CardContent>
                  <ScrollArea className="h-64">
                    <div className="space-y-3">
                      {dashboardData?.studies_health?.map((study, idx) => (
                        <div key={idx} className="p-3 rounded-lg bg-slate-100 hover:bg-slate-200 transition-colors cursor-pointer" onClick={() => setSelectedStudy(study.study_id)}>
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-medium text-slate-900">{study.study_name}</span>
                            <Badge className={getReadinessBgColor(study.inspection_readiness)}>
                              {study.inspection_readiness?.replace('_', ' ')}
                            </Badge>
                          </div>
                          <div className="flex items-center gap-3">
                            <Progress value={study.completeness} className="flex-1 h-2" />
                            <span className="text-sm font-medium text-slate-700 w-12">{study.completeness}%</span>
                          </div>
                          {study.critical_count > 0 && (
                            <p className="text-xs text-red-600 mt-1">{study.critical_count} missing critical documents</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                </CardContent>
              </Card>

              {/* Status Distribution Pie */}
              <Card className="bg-slate-50/90 border-slate-300 backdrop-blur-sm">
                <CardHeader>
                  <CardTitle className="text-slate-900 flex items-center gap-2">
                    <Activity className="w-5 h-5 text-purple-600" />
                    Inspection Readiness Distribution
                  </CardTitle>
                  <CardDescription>Status across all studies</CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={250}>
                    <PieChart>
                      <Pie
                        data={getStatusPieData()}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={90}
                        paddingAngle={2}
                        label={({ name, value }) => `${name}: ${value}`}
                      >
                        {getStatusPieData().map((entry, index) => (
                          <Cell key={index} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </div>

            {/* Critical Alerts */}
            {dashboardData?.critical_alerts?.length > 0 && (
              <Card className="bg-slate-50/90 border-slate-300 backdrop-blur-sm">
                <CardHeader>
                  <CardTitle className="text-slate-900 flex items-center gap-2">
                    <AlertCircle className="w-5 h-5 text-red-600" />
                    Critical Missing Documents
                  </CardTitle>
                  <CardDescription>Top priority items requiring immediate attention</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-3">
                    {dashboardData.critical_alerts.slice(0, 6).map((alert, idx) => (
                      <div key={idx} className="p-3 rounded-lg bg-red-50 border border-red-200">
                        <div className="flex items-start gap-2">
                          <XCircle className="w-5 h-5 text-red-600 mt-0.5" />
                          <div>
                            <p className="font-medium text-slate-900">{alert.document_type}</p>
                            <p className="text-sm text-slate-600">{alert.study_name} • {alert.zone_name}</p>
                            {alert.days_overdue > 0 && (
                              <p className="text-xs text-red-600 mt-1">{alert.days_overdue} days overdue</p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Zone Details Tab */}
          <TabsContent value="zones" className="space-y-4" data-testid="zones-content">
            {selectedStudy === 'all' ? (
              <Card className="bg-slate-50/90 border-slate-300 backdrop-blur-sm">
                <CardContent className="p-12 text-center">
                  <Info className="w-16 h-16 text-blue-500 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-slate-900 mb-2">Select a Study</h3>
                  <p className="text-slate-600">Select a specific study from the dropdown to view zone-by-zone health details</p>
                </CardContent>
              </Card>
            ) : studyHealth ? (
              <>
                {/* Overall Study Health */}
                <Card className="bg-slate-50/90 border-slate-300 backdrop-blur-sm">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <h2 className="text-2xl font-bold text-slate-900">{studyHealth.study_name}</h2>
                        <p className="text-slate-600">Phase: {studyHealth.study_phase} • {studyHealth.total_documents} documents</p>
                      </div>
                      <div className="text-right">
                        <p className="text-4xl font-bold text-slate-900">{studyHealth.overall_completeness}%</p>
                        <Badge className={`text-lg ${getReadinessBgColor(studyHealth.inspection_readiness)}`}>
                          {studyHealth.inspection_readiness?.replace('_', ' ').toUpperCase()}
                        </Badge>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Zone Bar Chart */}
                <Card className="bg-slate-50/90 border-slate-300 backdrop-blur-sm">
                  <CardHeader>
                    <CardTitle className="text-slate-900">Zone Completeness</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={getZoneBarData()} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                        <XAxis type="number" domain={[0, 100]} stroke="#64748B" />
                        <YAxis dataKey="zone" type="category" stroke="#64748B" width={100} />
                        <Tooltip formatter={(value) => `${value}%`} />
                        <Bar dataKey="completeness" radius={[0, 4, 4, 0]}>
                          {getZoneBarData().map((entry, index) => (
                            <Cell key={index} fill={entry.fill} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>

                {/* Zone Cards */}
                <div className="grid grid-cols-3 gap-4">
                  {Object.entries(studyHealth.zone_health || {}).map(([zoneId, zone]) => {
                    const StatusIcon = STATUS_ICONS[zone.status] || AlertTriangle;
                    return (
                      <Card key={zoneId} className={`bg-slate-50/90 border-slate-300 backdrop-blur-sm ${zone.status === 'critical' ? 'border-red-300' : ''}`}>
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2">
                              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: ZONE_COLORS[zoneId] }}></div>
                              <h4 className="font-semibold text-slate-900">{zone.name}</h4>
                            </div>
                            <StatusIcon className={`w-5 h-5 ${zone.status === 'complete' ? 'text-green-600' : zone.status === 'incomplete' ? 'text-amber-600' : 'text-red-600'}`} />
                          </div>
                          <div className="space-y-2">
                            <div className="flex justify-between text-sm">
                              <span className="text-slate-600">Completeness</span>
                              <span className="font-medium">{zone.completeness}%</span>
                            </div>
                            <Progress value={zone.completeness} className="h-2" />
                            <div className="flex justify-between text-xs text-slate-500">
                              <span>{zone.found_required}/{zone.expected_required} required docs</span>
                              <span>{zone.total_documents} total</span>
                            </div>
                            {zone.missing_documents?.length > 0 && (
                              <div className="mt-2 pt-2 border-t border-slate-200">
                                <p className="text-xs text-red-600 font-medium">Missing:</p>
                                {zone.missing_documents.slice(0, 3).map((doc, idx) => (
                                  <p key={idx} className="text-xs text-slate-600">• {doc.type}</p>
                                ))}
                              </div>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </>
            ) : (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
              </div>
            )}
          </TabsContent>

          {/* Alerts Tab */}
          <TabsContent value="alerts" className="space-y-4" data-testid="alerts-content">
            {alerts.length === 0 ? (
              <Card className="bg-slate-50/90 border-slate-300 backdrop-blur-sm">
                <CardContent className="p-12 text-center">
                  <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-slate-900 mb-2">No Active Alerts</h3>
                  <p className="text-slate-600">All studies are meeting TMF requirements</p>
                </CardContent>
              </Card>
            ) : (
              <ScrollArea className="h-[600px]">
                <div className="space-y-3">
                  {alerts.map((alert, idx) => (
                    <Card key={idx} className={`bg-slate-50/90 border backdrop-blur-sm ${alert.severity === 'critical' ? 'border-red-300' : 'border-slate-300'}`}>
                      <CardContent className="p-4">
                        <div className="flex items-start gap-4">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${SEVERITY_COLORS[alert.severity]}`}>
                            {alert.severity === 'critical' || alert.severity === 'high' ? 
                              <XCircle className="w-5 h-5" /> : <AlertTriangle className="w-5 h-5" />}
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <Badge className={SEVERITY_COLORS[alert.severity]}>{alert.severity}</Badge>
                              <span className="text-sm text-slate-500">{alert.type?.replace(/_/g, ' ')}</span>
                            </div>
                            <p className="font-medium text-slate-900">{alert.message}</p>
                            {alert.study_name && (
                              <p className="text-sm text-slate-600 mt-1">Study: {alert.study_name}</p>
                            )}
                            {alert.recommendation && (
                              <p className="text-sm text-green-700 mt-2 bg-green-50 p-2 rounded">
                                <strong>Action:</strong> {alert.recommendation}
                              </p>
                            )}
                          </div>
                          <Button variant="ghost" size="sm" onClick={() => alert.study_id && setSelectedStudy(alert.study_id)}>
                            <ChevronRight className="w-5 h-5" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </ScrollArea>
            )}
          </TabsContent>

          {/* Missing Documents Tab */}
          <TabsContent value="missing" className="space-y-4" data-testid="missing-content">
            {dashboardData?.critical_alerts?.length === 0 ? (
              <Card className="bg-slate-50/90 border-slate-300 backdrop-blur-sm">
                <CardContent className="p-12 text-center">
                  <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-slate-900 mb-2">No Missing Critical Documents</h3>
                  <p className="text-slate-600">All required documents are present</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {/* Group by study */}
                {studies.map(study => {
                  const studyAlerts = dashboardData?.critical_alerts?.filter(a => a.study_id === study.id) || [];
                  if (studyAlerts.length === 0) return null;
                  
                  return (
                    <Card key={study.id} className="bg-slate-50/90 border-slate-300 backdrop-blur-sm">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-lg text-slate-900">{study.name}</CardTitle>
                        <CardDescription>{studyAlerts.length} missing documents</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-2 gap-2">
                          {studyAlerts.map((alert, idx) => (
                            <div key={idx} className="flex items-center gap-2 p-2 rounded bg-red-50 border border-red-200">
                              <FileText className="w-4 h-4 text-red-600" />
                              <div className="flex-1">
                                <p className="text-sm font-medium text-slate-900">{alert.document_type}</p>
                                <p className="text-xs text-slate-500">{alert.zone_name}</p>
                              </div>
                              {alert.days_overdue > 0 && (
                                <Badge className="bg-red-500 text-white text-xs">{alert.days_overdue}d</Badge>
                              )}
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
